import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { EmptySection, HeadSection } from 'src/components/elements'
import { Button } from 'src/components/ui'
import { useDeletePropertySearchMutation, useGetPropertySearchesQuery } from 'src/redux/api'
import { PropertiesTab } from './tabs'
import SectionHint from 'src/components/elements/section-hint/SectionHint'
import { Skeleton } from '@mui/material'
import { CreateRequestModal } from 'src/components/elements/modals'
import ErrorState from 'src/components/elements/error-state/ErrorState'

interface IPropertyList {
  // On the unified home page an empty section disappears instead of pushing
  // its own CTA — the hero already carries it (38.2)
  hideWhenEmpty?: boolean
  // Rendered inside the unified RequestsInbox (D2/D3): the inbox owns the
  // heading and the create action, so hide the section's own header/hint.
  embedded?: boolean
}

type TabPropertiesOptions = 'all' | string

const PropertyList: FC<IPropertyList> = ({ hideWhenEmpty, embedded }) => {
  const { t } = useTranslation()
  const { data: tabs, isLoading, isError, refetch } = useGetPropertySearchesQuery(undefined)
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<TabPropertiesOptions>('all')

  // Deep-link from the profile "offers" button: /?tab=<searchId> pre-selects
  // that search's tab once the tabs have loaded, then the param is cleared.
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam && tabs?.some((tab: any) => tab.id === tabParam)) {
      setActiveTab(tabParam)
      searchParams.delete('tab')
      setSearchParams(searchParams, { replace: true })
    }
  }, [tabs, searchParams, setSearchParams])
  const [isEmpty, setIsEmpty] = useState(true)
  const [isOpenModal, setIsOpenModal] = useState(false)
  // Editing a specific saved search reuses the create modal (D2/D3: request
  // management moved into the outgoing inbox from the old profile section).
  const [editId, setEditId] = useState<string | undefined>(undefined)
  const [deleteSearch] = useDeletePropertySearchMutation()
  const handleCloseModal = useCallback(() => setIsOpenModal(false), [])
  const handleOpenModal = useCallback(() => {
    setEditId(undefined)
    setIsOpenModal(true)
  }, [])
  const handleEditActive = useCallback(() => {
    if (activeTab !== 'all') {
      setEditId(activeTab)
      setIsOpenModal(true)
    }
  }, [activeTab])
  const handleDeleteActive = useCallback(async () => {
    if (activeTab === 'all') return
    try {
      await deleteSearch(activeTab).unwrap()
      setActiveTab('all')
    } catch {
      // delete errors are surfaced by the api layer
    }
  }, [activeTab, deleteSearch])

  // Create tabs array with "All" tab at the beginning
  const tabsWithAll = useMemo(() => {
    if (!tabs || tabs.length === 0) return []

    const totalCount = tabs.reduce((acc: number, tab: any) => acc + (tab.count || 0), 0)

    return [{ id: 'all', label: t('all'), count: totalCount }, ...tabs]
  }, [tabs, t])

  useEffect(() => {
    if (tabs) {
      setIsEmpty(tabs?.length === 0)
    }
  }, [tabs])

  if (isError) {
    return <ErrorState onRetry={refetch} />
  }

  if (hideWhenEmpty && !isLoading && isEmpty) {
    return null
  }

  return (
    <section className="flex flex-col gap-4">
      {!embedded && (
        <>
          <HeadSection title={t('offers')} isSearch isSorting />
          <SectionHint storageKey="offers-section" text={t('hints.offers_section')} />
        </>
      )}
      <CreateRequestModal open={isOpenModal} handleClose={handleCloseModal} editId={editId} />

      {isLoading ? (
        <Skeleton variant="rounded" height={503} />
      ) : isEmpty ? (
        <EmptySection onClick={handleOpenModal} />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <nav className="flex overflow-x-auto  md:m-0 md:p-0 -mx-4 px-4 pr-4">
              {tabsWithAll &&
                tabsWithAll.map((tab: any) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as TabPropertiesOptions)}
                    className={clsx(
                      'shrink-0 h-11 px-4 text-center min-w-[6.4375rem] relative inline-flex gap-4 text-base items-center justify-center transition-all duration-300',
                      activeTab === tab.id
                        ? 'text-brand-ink font-extrabold'
                        : 'text-ink font-bold hover:text-brand-ink',
                    )}
                  >
                    <span>{tab.label}</span>
                    {tab.count !== undefined && (
                      <span
                        className={clsx(
                          'inline-flex items-center justify-center text-center p-1 min-w-6 h-6 text-xs font-bold text-ink rounded-full border border-solid border-ink/[0.18] transition-all duration-300',
                          activeTab === tab.id && '!border-brand-strong bg-brand-strong text-white',
                        )}
                      >
                        {tab.count}
                      </span>
                    )}
                    <span
                      className={`h-0.5 bg-brand absolute bottom-0 left-1/2 -translate-x-1/2 z-10 pointer-events-none transition-all duration-300 ${
                        activeTab === tab.id ? 'w-full' : 'w-0'
                      }`}
                    />
                  </button>
                ))}
            </nav>
            <hr className="shrink-0 self-stretch h-px m-0 border-0 bg-ink/[0.12] w-full -mt-px" />
          </div>
          {/* Manage the selected saved search inline (D2/D3): edit reuses the
              request modal, delete drops it and falls back to the "All" view. */}
          {embedded && activeTab !== 'all' && (
            <div className="flex gap-2">
              <Button variant="outlined" className="!h-9 !py-1" onClick={handleEditActive}>
                {t('requests_inbox.edit')}
              </Button>
              <Button
                variant="outlined"
                className="!h-9 !py-1 !border-danger !text-danger !fill-danger hover:!bg-danger-strong hover:!text-white"
                onClick={handleDeleteActive}
              >
                {t('requests_inbox.delete')}
              </Button>
            </div>
          )}
          <PropertiesTab key={activeTab} propertySearchId={activeTab} />
        </div>
      )}
    </section>
  )
}

export default PropertyList
